/**
 * Domain Setup integration component
 * Handles custom domain configuration with DIY guides and remote access options
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Globe, Shield, ExternalLink, Star, Zap, TrendingUp, Save, CheckCircle, AlertCircle, Wrench, Users, Info, Copy, Settings, ChevronRight, Mail, ChevronDown } from 'lucide-react';

import { useOnboarding } from '@/hooks/useOnboarding';
import { OnboardingSection, OnboardingStatus, SetupMethod, DomainSetupFormData } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';
import IntegrationSetup from './IntegrationSetup';

// Validation schema
const domainSetupSchema = z.object({
  dns_setup_method: z.nativeEnum(SetupMethod),
  root_domain: z.string().optional(),
  custom_sub_domain: z.string().optional(),
  review_management_subdomain: z.string().optional(),
  dns_provider_username: z.string().optional(),
  dns_provider_password: z.string().optional(),
  dns_provider_name: z.string().optional(),
  dns_provider_custom_name: z.string().optional(),
}).refine((data) => {
  if (data.dns_setup_method === SetupMethod.DIY) {
    return data.root_domain && data.custom_sub_domain;
  }
  return true;
}, {
  message: "Domain details are required for DIY setup",
  path: ["root_domain"],
});

type DomainSetupFormValues = z.infer<typeof domainSetupSchema>;

interface DomainSetupProps {
  onNavigateToSection?: (section: string) => void;
}

const DomainSetup: React.FC<DomainSetupProps> = ({ onNavigateToSection }) => {
  const {
    onboardingData,
    getSectionData,
    getSectionStatus,
    validateSection,
    updateSection,
    markSectionComplete,
    isSaving,
    isMarkingComplete,
  } = useOnboarding();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [remoteAccessSubTab, setRemoteAccessSubTab] = useState('temporary-access');
  const [diySubTab, setDiySubTab] = useState('configure-dns');
  const [domainFormExpanded, setDomainFormExpanded] = useState(false);
  const [dnsRecordsExpanded, setDnsRecordsExpanded] = useState(false);
  const [showEmailContent, setShowEmailContent] = useState(false);
  const [showNameserverEmailContent, setShowNameserverEmailContent] = useState(false);

  const sectionStatus = getSectionStatus(OnboardingSection.DOMAIN);
  const sectionData = useMemo(() => getSectionData(OnboardingSection.DOMAIN), [onboardingData]);
  const currentSetupMethod = sectionData?.dns_setup_method || SetupMethod.DIY;

  const form = useForm<DomainSetupFormValues>({
    resolver: zodResolver(domainSetupSchema),
    defaultValues: {
      dns_setup_method: SetupMethod.DIY,
      root_domain: '',
      custom_sub_domain: '',
      review_management_subdomain: '',
      dns_provider_username: '',
      dns_provider_password: '',
      dns_provider_name: '',
      dns_provider_custom_name: '',
    },
  });

  // Watch the form's current setup method for tab state
  const activeSetupMethod = form.watch('dns_setup_method');

  // Load existing data
  useEffect(() => {
    if (sectionData) {
      form.reset({
        dns_setup_method: sectionData.dns_setup_method || SetupMethod.DIY,
        root_domain: sectionData.root_domain || '',
        custom_sub_domain: sectionData.custom_sub_domain || '',
        review_management_subdomain: sectionData.review_management_subdomain || '',
        dns_provider_username: sectionData.dns_provider_username || '',
        dns_provider_password: sectionData.dns_provider_password || '',
        dns_provider_name: sectionData.dns_provider_name || '',
        dns_provider_custom_name: sectionData.dns_provider_custom_name || '',
      });
      setHasUnsavedChanges(false);
    }
  }, [sectionData, form.reset]);

  // Track form changes manually (no autosave)
  useEffect(() => {
    const subscription = form.watch(() => {
      setHasUnsavedChanges(true);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [form.watch]);

  // Handle setup method change
  const handleSetupMethodChange = (method: SetupMethod) => {
    form.setValue('dns_setup_method', method);
    setHasUnsavedChanges(true);
  };

  // Save section data
  const handleSave = async () => {
    try {
      const formData = form.getValues();
      await updateSection(OnboardingSection.DOMAIN, formData);
      setHasUnsavedChanges(false);
      toast({
        title: "Saved",
        description: "Domain setup saved successfully"
      });
    } catch (error) {
      console.error('Error saving domain setup:', error);
      toast({
        title: "Error",
        description: "Failed to save domain setup",
        variant: "destructive"
      });
    }
  };

  // Mark section as complete
  const handleMarkComplete = async () => {
    try {
      const formData = form.getValues();
      await updateSection(OnboardingSection.DOMAIN, formData);
      await markSectionComplete(OnboardingSection.DOMAIN);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error marking domain setup complete:', error);
    }
  };

  // Check if can mark complete
  const canMarkComplete = () => {
    const values = form.getValues();
    if (values.dns_setup_method === SetupMethod.DIY) {
      return !!(values.root_domain && values.custom_sub_domain);
    }
    return true; // Remote access can always be marked complete
  };

  // Get the full domain preview
  const getFullDomain = () => {
    const values = form.getValues();
    if (values.custom_sub_domain && values.root_domain) {
      return `${values.custom_sub_domain}.${values.root_domain}`;
    }
    return '';
  };

  // Get the review management domain preview
  const getReviewManagementDomain = () => {
    const values = form.getValues();
    if (values.review_management_subdomain && values.root_domain) {
      return `${values.review_management_subdomain}.${values.root_domain}`;
    }
    return '';
  };

  // Check if Step 1 is completed (has required domain information)
  const isStep1Completed = () => {
    const values = form.getValues();
    return !!(values.root_domain && values.custom_sub_domain);
  };

  // Generate email content for developer
  const generateDeveloperEmail = () => {
    if (!isStep1Completed()) {
      toast({
        title: "Step 1 Required",
        description: "Please complete Step 1 by providing your domain name and preferred subdomain(s) before generating the developer email.",
        variant: "destructive",
      });
      return;
    }
    setShowEmailContent(!showEmailContent);
  };

  // Generate email subject
  const getEmailSubject = () => {
    const values = form.getValues();
    return `DNS Setup Required for ${values.root_domain} - Pump'd Integration`;
  };

  // Generate email content
  const getEmailContent = () => {
    const values = form.getValues();
    const orderingDomain = getFullDomain();
    const reviewDomain = getReviewManagementDomain();
    
    return `Hi there,

We need your help setting up DNS records for our Pump'd online ordering integration. Please add the following DNS records to our domain:

Domain: ${values.root_domain}

DNS RECORDS TO ADD:

1. Ordering Page DNS Record (Required)
   Type: A
   Name: ${values.custom_sub_domain}
   Value: ${sectionData?.ordering_subdomain_ip || '35.238.2.132'}
   TTL: 1 min (or as low as possible)
   
   This will create: https://${orderingDomain}

${reviewDomain ? `2. Review Management DNS Record (Required)
   Type: A  
   Name: ${values.review_management_subdomain}
   Value: ${sectionData?.review_management_ip || '172.66.0.70'}
   TTL: 1 min (or as low as possible)
   
   This will create: https://${reviewDomain}

` : ''}INSTRUCTIONS:

1. Log in to our domain provider/DNS management panel
2. Look for "DNS Management", "DNS Records", or "Name Servers" section
3. Add the A records listed above
4. Verify the records are active (this usually takes a few minutes)
5. Once records are active, please confirm completion

HELPFUL RESOURCES:
- Cloudflare DNS Guide: https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/
- GoDaddy DNS Guide: https://godaddy.com/help/add-an-a-record-19238

IMPORTANT: Please confirm once the DNS records are active. We can set up most of the domain configuration immediately, but the SSL certificate generation must wait until the DNS records have propagated.

Thanks for your help!

Best regards,
The Pump'd Team`;
  };

  // Review management info hover dialog component
  const ReviewManagementInfoDialog = () => (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-auto p-1 text-blue-600 hover:text-blue-800">
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-3">
          <div>
            <h4 className="font-medium">Pump'd review management helps you improve your Google Business Rating</h4>
            <p className="text-sm text-muted-foreground">Get more 5-star reviews, handle negative reviews offline, improve your SEO</p>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span className="text-sm">Request reviews via QR codes on receipts, Email and SMS</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span className="text-sm">Handle negative feedback before it hits the web</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span className="text-sm">Send 5-star reviewers to your Google Business Profile</span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  // Copyable DNS record component
  const CopyableDNSRecord = ({ type, name, value, description }: { 
    type: string; 
    name: string; 
    value: string; 
    description: string; 
  }) => {
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const handleCopy = async (text: string, field: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    };

    return (
      <div className="bg-gray-50 p-4 rounded border">
        <div className="flex items-center justify-between mb-3">
          <h5 className="font-medium text-sm">{description}</h5>
          <Badge variant="secondary" className="text-xs">Required</Badge>
        </div>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium w-12 flex-shrink-0">Type:</span>
              <span className="font-mono">{type}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleCopy(type, 'type')}
              className="h-6 w-6 p-0 flex-shrink-0 ml-2"
            >
              {copiedField === 'type' ? (
                <CheckCircle className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium w-12 flex-shrink-0">Name:</span>
              <span className="font-mono">{name}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleCopy(name, 'name')}
              className="h-6 w-6 p-0 flex-shrink-0 ml-2"
            >
              {copiedField === 'name' ? (
                <CheckCircle className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium w-12 flex-shrink-0">Value:</span>
              <span className="font-mono text-blue-600">{value}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleCopy(value, 'value')}
              className="h-6 w-6 p-0 flex-shrink-0 ml-2"
            >
              {copiedField === 'value' ? (
                <CheckCircle className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium w-12 flex-shrink-0">TTL:</span>
              <span className="font-mono">1 min (or as low as possible)</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleCopy('60', 'ttl')}
              className="h-6 w-6 p-0 flex-shrink-0 ml-2"
            >
              {copiedField === 'ttl' ? (
                <CheckCircle className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Copyable Nameserver component
  const CopyableNameserver = ({ nameserver, label }: { nameserver: string; label: string }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(nameserver);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    };

    return (
      <div className="bg-gray-50 p-3 rounded border flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground font-medium">{label}</div>
          <div className="font-mono text-sm">{nameserver}</div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleCopy}
          className="h-8 px-3"
        >
          {copied ? (
            <>
              <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </>
          )}
        </Button>
      </div>
    );
  };

  // Copyable Email Content component
  const CopyableEmailContent = ({ title, content }: { title: string; content: string }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    };

    return (
      <div className="bg-gray-50 p-4 rounded border">
        <div className="flex items-center justify-between mb-2">
          <h5 className="font-medium text-sm">{title}</h5>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleCopy}
            className="h-8 px-3"
          >
            {copied ? (
              <>
                <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>
        <pre className="text-sm whitespace-pre-wrap bg-white p-3 rounded border font-mono">
          {content}
        </pre>
      </div>
    );
  };



  // Remote access instructions with choice structure
  const remoteInstructions = [
    {
      type: 'step_with_form',
      number: '1',
      text: "Provide your domain name and preferred subdomain(s)",
      expandable: true
    },
    {
      type: 'choice_header',
      number: '2',
      text: "Choose ONE of the following options:"
    },
    {
      type: 'choice_option',
      number: '2a',
      text: "Give us temporary access to your DNS management panel (login details)",
      tabLink: 'temporary-access'
    },
    {
      type: 'choice_option', 
      number: '2b',
      text: "Update your nameservers to let us manage your DNS settings without logging in to your account",
      tabLink: 'nameserver-access'
    }
  ];

  // DIY instructions with choice structure  
  const diyInstructions = [
    {
      type: 'step_with_form',
      number: '1',
      text: "Provide your domain name and preferred subdomain(s)",
      expandable: true
    },
    {
      type: 'choice_header',
      number: '2',
      text: "Choose ONE of the following options:"
    },
    {
      type: 'choice_option',
      number: '2a',
      text: "Configure DNS yourself",
      tabLink: 'configure-dns'
    },
    {
      type: 'choice_option', 
      number: '2b',
      text: "Send instructions to your web developer",
      tabLink: 'send-to-developer'
    }
  ];

  // Remote access form
  const remoteFormContent = (
    <Form {...form}>
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="root_domain"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Your Root Domain</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., yourrestaurant.com"
                  value={field.value || ''}
                />
              </FormControl>
              <FormDescription>
                Your main website domain (without www or https)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="custom_sub_domain"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Subdomain for your Online Ordering</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., order, online, menu"
                  value={field.value || ''}
                />
              </FormControl>
              <FormDescription>
                The subdomain where customers will place orders
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="review_management_subdomain"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                Subdomain for your Review Management Page
                <ReviewManagementInfoDialog />
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., review, reviews, ratings"
                  value={field.value || ''}
                />
              </FormControl>
              <FormDescription>
                The subdomain for your review management page (if using Pump'd review management add-on)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {(getFullDomain() || getReviewManagementDomain()) && (
          <div className="space-y-3">
            {getFullDomain() && (
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <h5 className="font-medium text-green-900 mb-1">Your Ordering Domain Will Be:</h5>
                <code className="text-green-800 font-medium">
                  https://{getFullDomain()}
                </code>
              </div>
            )}
            {getReviewManagementDomain() && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <h5 className="font-medium text-blue-900 mb-1">Your Review Management Domain Will Be:</h5>
                <code className="text-blue-800 font-medium">
                  https://{getReviewManagementDomain()}
                </code>
              </div>
            )}
          </div>
        )}
      </div>
    </Form>
  );

  // Status badge component
  const StatusBadge = () => {
    switch (sectionStatus) {
      case OnboardingStatus.COMPLETE:
        return <Badge className="bg-green-100 text-green-800">Complete</Badge>;
      case OnboardingStatus.AWAITING_CONFIRMATION:
        return <Badge className="bg-yellow-100 text-yellow-800">Under Review</Badge>;
      case OnboardingStatus.NEEDS_ATTENTION:
        return <Badge className="bg-red-100 text-red-800">Needs Attention</Badge>;
      default:
        return <Badge variant="secondary">Not Started</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Globe className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-xl font-semibold">Custom Domain Setup</h2>
            <p className="text-sm text-muted-foreground">
              Set up a custom domain for your online ordering system
            </p>
          </div>
        </div>
        <StatusBadge />
      </div>

      {/* Status alerts */}
      {sectionStatus === OnboardingStatus.NEEDS_ATTENTION && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This section needs your attention. Please review the feedback from our team and make the necessary changes.
          </AlertDescription>
        </Alert>
      )}

      {sectionStatus === OnboardingStatus.AWAITING_CONFIRMATION && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Your domain configuration has been submitted for review. Our team will verify the DNS settings and contact you if any changes are needed.
          </AlertDescription>
        </Alert>
      )}

      {sectionStatus === OnboardingStatus.COMPLETE && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            Your custom domain has been successfully configured and verified. Your ordering system is now accessible at your custom domain.
          </AlertDescription>
        </Alert>
      )}

      {/* What are DNS records */}
      <Card>
        <CardHeader 
          className="cursor-pointer"
          onClick={() => setDnsRecordsExpanded(!dnsRecordsExpanded)}
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-base flex items-center gap-2">
                <Globe className="h-5 w-5" />
                What are DNS records?
              </h4>
              <p className="text-sm text-muted-foreground">
                Understanding how custom domains work with Pump'd
              </p>
            </div>
            <div className={`transform transition-transform ${dnsRecordsExpanded ? 'rotate-90' : ''}`}>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </CardHeader>
        {dnsRecordsExpanded && (
          <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            While it's possible to host your online ordering and review management pages on a Pump'd domain (order-yourrestaurant.pumpd.co.nz & review-yourrestaurant.pumpd.co.nz), we recommend adding custom subdomains (order.yourrestaurant.co.nz & review.yourrestaurant.co.nz). This results in a better experience for your customers, as they never have to leave your site. Plus, it improves your SEO ranking (your website will show up more on Google) - which means more customers will find your restaurant and you'll get more orders!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <Star className="h-5 w-5 text-blue-600 mt-1" />
              <div>
                <h4 className="font-medium text-sm text-blue-900">Improved Brand Image</h4>
                <p className="text-xs text-blue-700">
                  Customers never leave your site and stay with your brand throughout the online ordering experience
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
              <TrendingUp className="h-5 w-5 text-green-600 mt-1" />
              <div>
                <h4 className="font-medium text-sm text-green-900">Better Ranking on Google</h4>
                <p className="text-xs text-green-700">
                  Get more orders by improving your SEO ranking and showing up on Google before your competitors
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
              <Zap className="h-5 w-5 text-purple-600 mt-1" />
              <div>
                <h4 className="font-medium text-sm text-purple-900">Easy Setup</h4>
                <p className="text-xs text-purple-700">
                  Follow our step-by-step guides to make DNS management a breeze
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <h4 className="font-medium">What you'll need:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Access to your domain registrar (where you purchased your domain name)
              </li>
            </ul>
            
            <div className="space-y-2">
              <p className="text-sm font-medium">Common domain registrars are:</p>
              <div className="space-y-1">
                <a
                  href="https://godaddy.com/help/add-an-a-record-19238"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  GoDaddy
                </a>
                <a
                  href="https://www.namecheap.com/support/knowledgebase/article.aspx/319/2237/how-can-i-set-up-an-a-address-record-for-my-domain/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Namecheap
                </a>
                <a
                  href="https://www.freeparking.co.nz/login/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Free Parking
                </a>
                <a
                  href="https://www.crazydomains.co.nz/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Crazy Domains
                </a>
              </div>
            </div>
          </div>
        </CardContent>
        )}
      </Card>

      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <Tabs value={activeSetupMethod} onValueChange={(value) => handleSetupMethodChange(value as SetupMethod)} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value={SetupMethod.DIY} className="flex items-center gap-2 data-[state=active]:text-white group">
                  <Wrench className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                  DIY Setup
                </TabsTrigger>
                <TabsTrigger value={SetupMethod.REMOTE_ACCESS} className="flex items-center gap-2 data-[state=active]:text-white group">
                  <Users className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                  Remote Access
                </TabsTrigger>
              </TabsList>

              <TabsContent value={SetupMethod.DIY} className="space-y-6 mt-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <Wrench className="h-5 w-5 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-blue-900">Do It Yourself</h4>
                      <p className="text-sm text-blue-700">
                        Follow our step-by-step guide to set up this integration yourself. 
                        You'll have full control over the process.
                      </p>
                    </div>
                  </div>

                  {/* Instructions */}
                  {diyInstructions.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium">Instructions:</h4>
                      <div className="space-y-4">
                        {diyInstructions.map((instruction, index) => {
                          if (instruction.type === 'step_with_form') {
                            return (
                              <div key={index} className="space-y-3">
                                <div 
                                  className={`flex items-center gap-4 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                    domainFormExpanded 
                                      ? 'border-green-300 bg-green-50' 
                                      : 'border-gray-200 bg-gray-50 hover:border-green-200'
                                  }`}
                                  onClick={() => setDomainFormExpanded(!domainFormExpanded)}
                                >
                                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                                    domainFormExpanded 
                                      ? 'bg-brand-green text-white' 
                                      : 'bg-brand-blue text-white'
                                  }`}>
                                    {instruction.number}
                                  </div>
                                  <div className="flex-1">
                                    <span className={`text-sm ${domainFormExpanded ? 'text-green-600 font-medium' : 'text-muted-foreground'}`}>
                                      {instruction.text}
                                    </span>
                                  </div>
                                  <div className={`flex-shrink-0 transform transition-transform ${domainFormExpanded ? 'rotate-90 text-green-500' : 'text-gray-400'}`}>
                                    <ChevronRight className="h-4 w-4" />
                                  </div>
                                </div>
                                
                                {domainFormExpanded && (
                                  <div className="ml-10 p-2 bg-white rounded-lg">
                                    {remoteFormContent}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          if (instruction.type === 'choice_header') {
                            return (
                              <div key={index} className="space-y-3 mt-8 mb-8">
                                <div className="flex items-center gap-4 p-3">
                                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-semibold">
                                    {instruction.number}
                                  </div>
                                  <div className="flex-1">
                                    <span className="text-sm font-medium">{instruction.text}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          if (instruction.type === 'choice_option') {
                            const isActive = diySubTab === instruction.tabLink;
                            return (
                              <div key={index}>
                                <div 
                                  className={`flex items-center gap-4 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                    isActive 
                                      ? 'border-green-300 bg-green-50' 
                                      : 'border-gray-200 bg-gray-50 hover:border-green-200'
                                  }`}
                                  onClick={() => setDiySubTab(instruction.tabLink)}
                                >
                                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                                    isActive 
                                      ? 'bg-brand-green text-white' 
                                      : 'bg-gray-300 text-gray-600'
                                  }`}>
                                    {instruction.number}
                                  </div>
                                  <div className="flex-1">
                                    <span className={`text-sm ${isActive ? 'text-green-600 font-medium' : 'text-muted-foreground'}`}>
                                      {instruction.text}
                                    </span>
                                  </div>
                                  <div className={`flex-shrink-0 transform transition-transform ${isActive ? 'rotate-90 text-blue-500' : 'text-gray-400'}`}>
                                    <ChevronRight className="h-4 w-4" />
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          return null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* DIY Subtabs */}
                  <Tabs value={diySubTab} onValueChange={setDiySubTab} className="space-y-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="configure-dns" className="flex items-center gap-2 group">
                        <Wrench className="h-4 w-4 group-data-[state=active]:stroke-white" />
                        Configure DNS myself
                      </TabsTrigger>
                      <TabsTrigger value="send-to-developer" className="flex items-center gap-2 group">
                        <Mail className="h-4 w-4 group-data-[state=active]:stroke-white" />
                        Send email to developer
                      </TabsTrigger>
                    </TabsList>

                    {/* Configure DNS Tab */}
                    <TabsContent value="configure-dns" className="space-y-4 pb-6">
                      <div className="space-y-4">
                        {/* Step 3: Access Domain Settings */}
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-semibold">3</div>
                          <div className="flex-1">
                            <div className="text-sm font-medium">Access Your Domain Settings</div>
                          </div>
                        </div>

                        <div className="ml-10">
                          <Alert>
                            <Settings className="h-4 w-4" />
                            <AlertDescription>
                            Log in to your domain provider (e.g., GoDaddy, Free Parking, Crazy Domains) and look for "DNS Management" or "DNS Records" in your domain control panel
                            </AlertDescription>
                          </Alert>
                        </div>

                        {/* Step 4: Create A Records */}
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-semibold">4</div>
                          <div className="flex-1">
                            <div className="text-sm font-medium">Create new "A" Records for each subdomain pointing to our servers:</div>
                          </div>
                        </div>

                        <div className="ml-10 space-y-4">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-4xl">
                            <CopyableDNSRecord
                              type="A"
                              name={form.watch('custom_sub_domain') || 'your-ordering-subdomain'}
                              value={sectionData?.ordering_subdomain_ip || '35.238.2.132'}
                              description="Ordering Page DNS Record"
                            />

                            {form.watch('review_management_subdomain') && (
                              <CopyableDNSRecord
                                type="A"
                                name={form.watch('review_management_subdomain')}
                                value={sectionData?.review_management_ip || '172.66.0.70'}
                                description="Review Page DNS Record"
                              />
                            )}
                          </div>

                          <div className="space-y-2">
                            <h5 className="font-medium">Helpful Links:</h5>
                            <div className="flex flex-col space-y-1">
                              <a
                                href="https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-primary hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Cloudflare DNS Guide
                              </a>
                              <a
                                href="https://godaddy.com/help/add-an-a-record-19238"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-primary hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                GoDaddy DNS Guide
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Step 5: Add ordering subdomain */}
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center">5</div>
                          <div className="flex-1">
                            <div className="text-sm font-medium">Add ordering subdomain to Pump'd Admin Dashboard</div>
                          </div>
                        </div>

                        <div className="ml-10 space-y-3">
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">1</div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">Navigate to your admin dashboard here:</span>
                            </div>
                          </div>
                          <div className="ml-9">
                            <Button asChild className="mt-2">
                              <a href="https://admin.pumpd.co.nz/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                                <ExternalLink className="h-4 w-4" />
                                Admin Dashboard
                              </a>
                            </Button>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">2</div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">Login with the Temporary Password sent to you by your account manager</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">3</div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">Navigate to Settings → Website → Additional Settings → Domain and click 'Custom Domain'</span>
                            </div>
                          </div>

                          <div>
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">4</div>
                              <span className="text-sm font-medium">Enter your Custom Domain</span>
                            </div>
                            <div className="ml-9">
                              {getFullDomain() && (
                                <div className="mt-2 p-2 bg-gray-50 border rounded flex items-center justify-between">
                                  <code className="text-sm font-mono">{getFullDomain()}</code>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => navigator.clipboard.writeText(getFullDomain())}
                                    className="h-6 text-xs"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">5</div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">Click 'Save'</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">6</div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">Click 'Generate SSL Certificate'</span>
                            </div>
                          </div>
                        </div>

                        {/* What happens next for Configure DNS */}
                        <div className="space-y-3 pb-4">
                          <h4 className="font-medium">What happens next:</h4>
                          <div className="space-y-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                                <CheckCircle className="h-4 w-4 stroke-white" />
                              </div>
                              <div className="flex-1">
                                <span>Your ordering page will become accessible from the subdomain you specified in step 1</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center cursor-pointer hover:bg-green-600 transition-colors" onClick={() => { onNavigateToSection?.(OnboardingSection.WEBSITE); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                                <ChevronRight className="h-4 w-4 stroke-white" />
                              </div>
                              <div className="flex-1">
                                <span>Proceed to website integration to let customers access this page from your website and start receiving orders! <a href="#" onClick={(e) => { e.preventDefault(); onNavigateToSection?.(OnboardingSection.WEBSITE); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-primary hover:underline">Go to Website Integration</a></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Send to Developer Tab */}
                    <TabsContent value="send-to-developer" className="space-y-4 pb-6">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium mb-2">Send Setup Instructions to Your Developer</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          We'll prepare a detailed email with all the technical requirements that you can forward to your web developer.
                        </p>
                        <Button 
                          onClick={generateDeveloperEmail}
                          className="flex items-center gap-2"
                        >
                          <Mail className="h-4 w-4" />
                          {showEmailContent ? (
                            <>
                              <ChevronDown className="h-4 w-4" />
                              Hide Email Content
                            </>
                          ) : (
                            'Generate Developer Email'
                          )}
                        </Button>
                      </div>

                      {showEmailContent && isStep1Completed() && (
                        <div className="space-y-4">
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <h4 className="font-medium text-green-900 mb-2">Email Ready to Send</h4>
                            <p className="text-sm text-green-700">
                              Copy the subject and email content below to send to your developer.
                            </p>
                          </div>

                          <CopyableEmailContent 
                            title="Email Subject"
                            content={getEmailSubject()}
                          />

                          <CopyableEmailContent 
                            title="Email Content"
                            content={getEmailContent()}
                          />

                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-700">
                              <strong>Tip:</strong> Copy both the subject and content, then paste them into your email client. 
                              Make sure to update your developer on the timeline and let them know to contact you once the DNS records are added.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Step 3: Admin Dashboard Setup - Always visible */}
                      <div className="space-y-4 mt-6">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <h4 className="font-medium text-blue-900 mb-2">📋 Step 3: Add Domain to Pump'd Admin Dashboard</h4>
                          <p className="text-sm text-blue-700">
                            You can complete steps 1-5 immediately. Wait to complete step 6 (Generate SSL Certificate) until after your developer confirms the DNS records are active.
                          </p>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center">3</div>
                          <div className="flex-1">
                            <div className="text-sm font-medium">Add ordering subdomain to Pump'd Admin Dashboard</div>
                            <div className="text-sm text-muted-foreground">Complete steps 1-5 now, then step 6 after DNS records are active</div>
                          </div>
                        </div>

                        <div className="ml-10 space-y-3">
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">1</div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">Navigate to your admin dashboard here:</span>
                            </div>
                          </div>
                          <div className="ml-9">
                            <Button asChild className="mt-2">
                              <a href="https://admin.pumpd.co.nz/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                                <ExternalLink className="h-4 w-4" />
                                Admin Dashboard
                              </a>
                            </Button>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">2</div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">Login with the Temporary Password sent to you by your account manager</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">3</div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">Navigate to Settings → Website → Additional Settings → Domain and click 'Custom Domain'</span>
                            </div>
                          </div>

                          <div>
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">4</div>
                              <span className="text-sm font-medium">Enter your Custom Domain</span>
                            </div>
                            <div className="ml-9">
                              {getFullDomain() && (
                                <div className="mt-2 p-2 bg-gray-50 border rounded flex items-center justify-between">
                                  <code className="text-sm font-mono">{getFullDomain()}</code>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => navigator.clipboard.writeText(getFullDomain())}
                                    className="h-6 text-xs"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">5</div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">Click 'Save'</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">6</div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-amber-900">Click 'Generate SSL Certificate'</span>
                            <div className="text-xs text-amber-700 mt-1">⚠️ Wait until DNS records are active before completing this step</div>
                          </div>
                        </div>

                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-700">
                            <strong>Steps 1-5:</strong> Complete these immediately after sending the email to your developer.<br/>
                            <strong>Step 6:</strong> Wait until your developer confirms the DNS records are active and have propagated (usually takes a few minutes to an hour).
                          </p>
                        </div>

                        {/* What happens next for Send to Developer */}
                        <div className="space-y-3 pb-4">
                          <h4 className="font-medium">What happens next:</h4>
                          <div className="space-y-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                                <CheckCircle className="h-4 w-4 stroke-white" />
                              </div>
                              <div className="flex-1">
                                <span>Your ordering page will become accessible from the subdomain you specified in step 1</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center cursor-pointer hover:bg-green-600 transition-colors" onClick={() => { onNavigateToSection?.(OnboardingSection.WEBSITE); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                                <ChevronRight className="h-4 w-4 stroke-white" />
                              </div>
                              <div className="flex-1">
                                <span>Proceed to website integration to let customers access this page from your website and start receiving orders! <a href="#" onClick={(e) => { e.preventDefault(); onNavigateToSection?.(OnboardingSection.WEBSITE); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-primary hover:underline">Go to Website Integration</a></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>

                </div>
              </TabsContent>

              <TabsContent value={SetupMethod.REMOTE_ACCESS} className="space-y-6 mt-6">
                <div className="space-y-6">
                  <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                    <Users className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-green-900">Remote Access Setup</h4>
                      <p className="text-sm text-green-700">
                        Our technical team will handle the complete domain setup including DNS configuration, SSL certificates, and testing. This is the recommended option if you're not familiar with DNS management.
                      </p>
                    </div>
                  </div>

                  {/* Instructions */}
                  {remoteInstructions.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium">Instructions:</h4>
                      <div className="space-y-4">
                        {remoteInstructions.map((instruction, index) => {
                          if (instruction.type === 'step') {
                            // Steps 3, 4, 5 are handled by the team (green with ticks)
                            const isTeamStep = ['3', '4', '5'].includes(instruction.number);
                            
                            return (
                              <div key={index} className="flex items-center gap-4">
                                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm ${
                                  isTeamStep 
                                    ? 'bg-brand-green text-white' 
                                    : 'bg-brand-blue text-white'
                                }`}>
                                  {isTeamStep ? (
                                    <CheckCircle className="h-4 w-4 stroke-white" />
                                  ) : (
                                    instruction.number
                                  )}
                                </div>
                                <div className="flex-1">
                                  <span className="text-sm text-medium font-medium">{instruction.text}</span>
                                </div>
                              </div>
                            );
                          }
                          
                          if (instruction.type === 'step_with_form') {
                            return (
                              <div key={index} className="space-y-3">
                                <div 
                                  className={`flex items-center gap-4 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                    domainFormExpanded 
                                      ? 'border-green-300 bg-green-50' 
                                      : 'border-gray-200 bg-gray-50 hover:border-green-200'
                                  }`}
                                  onClick={() => setDomainFormExpanded(!domainFormExpanded)}
                                >
                                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                                    domainFormExpanded 
                                      ? 'bg-brand-green text-white' 
                                      : 'bg-brand-blue text-white'
                                  }`}>
                                    {instruction.number}
                                  </div>
                                  <div className="flex-1">
                                    <span className={`text-sm ${domainFormExpanded ? 'text-green-600 font-medium' : 'text-muted-foreground'}`}>
                                      {instruction.text}
                                    </span>
                                  </div>
                                  <div className={`flex-shrink-0 transform transition-transform ${domainFormExpanded ? 'rotate-90 text-green-500' : 'text-gray-400'}`}>
                                    <ChevronRight className="h-4 w-4" />
                                  </div>
                                </div>
                                
                                {domainFormExpanded && (
                                  <div className="ml-10 p-2 bg-white rounded-lg">
                                    {remoteFormContent}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          if (instruction.type === 'choice_header') {
                            return (
                              <div key={index} className="space-y-3 mt-8 mb-8">
                                <div className="flex items-center gap-4 p-3">
                                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-semibold">
                                    {instruction.number}
                                  </div>
                                  <div className="flex-1">
                                    <span className="text-sm font-medium">{instruction.text}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          if (instruction.type === 'choice_option') {
                            const isActive = remoteAccessSubTab === instruction.tabLink;
                            return (
                              <div key={index}>
                                <div 
                                  className={`flex items-center gap-4 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                    isActive 
                                      ? 'border-green-300 bg-green-50' 
                                      : 'border-gray-200 bg-gray-50 hover:border-green-200'
                                  }`}
                                  onClick={() => setRemoteAccessSubTab(instruction.tabLink)}
                                >
                                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                                    isActive 
                                      ? 'bg-brand-green text-white' 
                                      : 'bg-gray-300 text-gray-600'
                                  }`}>
                                    {instruction.number}
                                  </div>
                                  <div className="flex-1">
                                    <span className={`text-sm ${isActive ? 'text-green-600 font-medium' : 'text-muted-foreground'}`}>
                                      {instruction.text}
                                    </span>
                                  </div>
                                  <div className={`flex-shrink-0 transform transition-transform ${isActive ? 'rotate-90 text-blue-500' : 'text-gray-400'}`}>
                                    <ChevronRight className="h-4 w-4" />
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          return null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Remote Access Subtabs */}
                  <Tabs value={remoteAccessSubTab} onValueChange={setRemoteAccessSubTab} className="space-y-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="temporary-access">Temporary Domain Access</TabsTrigger>
                      <TabsTrigger value="nameserver-access">Nameserver Access</TabsTrigger>
                    </TabsList>

                    {/* Temporary Domain Access Tab */}
                    <TabsContent value="temporary-access" className="space-y-4">
                      <Form {...form}>
                        <div className="space-y-4 p-4 bg-gray-50 rounded border">
                          <div className="space-y-4">
                            <FormField
                              control={form.control}
                              name="dns_provider_name"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>DNS Provider</FormLabel>
                                  <Select onValueChange={field.onChange} value={field.value}>
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select your DNS provider" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="godaddy">GoDaddy</SelectItem>
                                      <SelectItem value="freeparking">Free Parking</SelectItem>
                                      <SelectItem value="crazydomains">Crazy Domains</SelectItem>
                                      <SelectItem value="namecheap">Namecheap</SelectItem>
                                      <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {form.watch('dns_provider_name') === 'other' && (
                              <FormField
                                control={form.control}
                                name="dns_provider_custom_name"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Custom DNS Provider Name</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="Enter your DNS provider name"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                            <FormField
                              control={form.control}
                              name="dns_provider_username"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Username/Email</FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      type="text"
                                      placeholder="Your login username or email"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="dns_provider_password"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Password</FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      type="password"
                                      placeholder="Your login password"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                      </Form>

                      <div className="bg-amber-50 p-3 rounded border">
                        <h5 className="font-medium text-amber-900 mb-1">Security Note</h5>
                        <p className="text-sm text-amber-700">
                          Your credentials are encrypted and only used for this integration. We recommend changing 
                          your password after the setup is complete for added security.
                        </p>
                      </div>

                      {/* What happens next for Temporary Access */}
                      <div className="space-y-3 pb-4">
                        <h4 className="font-medium">What happens next:</h4>
                        <div className="space-y-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>Our team will securely access your DNS management panel</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>We'll configure your domain settings and SSL certificates</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>You'll receive confirmation when the setup is complete</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>Your ordering page will become accessible from the subdomain you specified in step 1</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center cursor-pointer hover:bg-green-600 transition-colors" onClick={() => { onNavigateToSection?.(OnboardingSection.WEBSITE); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                              <ChevronRight className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>Proceed to website integration to let customers access this page from your website and start receiving orders! <a href="#" onClick={(e) => { e.preventDefault(); onNavigateToSection?.(OnboardingSection.WEBSITE); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-primary hover:underline">Go to Website Integration</a></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Nameserver Access Tab */}
                    <TabsContent value="nameserver-access" className="space-y-4">
                      <div className="space-y-6">
                        {/* Send Instructions to Developer */}
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium mb-2">Send Nameserver Instructions to Your Developer</h4>
                          <p className="text-sm text-muted-foreground mb-4">
                            We'll prepare a detailed email with nameserver change instructions that you can forward to your web developer or technical team.
                          </p>
                          <Button 
                            onClick={() => setShowNameserverEmailContent(!showNameserverEmailContent)}
                            className="flex items-center gap-2"
                          >
                            <Mail className="h-4 w-4" />
                            {showNameserverEmailContent ? (
                              <>
                                <ChevronDown className="h-4 w-4" />
                                Hide Email Content
                              </>
                            ) : (
                              'Generate Developer Email'
                            )}
                          </Button>

                          {showNameserverEmailContent && (
                            <div className="mt-4 space-y-4">
                              <CopyableEmailContent 
                                title="Email Subject"
                                content="Action Required: Update Nameservers for Domain Setup"
                              />
                              <CopyableEmailContent 
                                title="Email Body"
                                content={`Hi,

I hope you're doing well. I need your assistance with updating the nameservers for our domain to complete our online ordering system setup.

Please make the following nameserver changes in our domain control panel:

Domain: ${form.watch('root_domain') || '[Your Domain]'}

Replace the existing nameservers with these Cloudflare nameservers:
• Nameserver 1: ${sectionData?.nameserver_1 || 'zod.ns.cloudflare.com'}
• Nameserver 2: ${sectionData?.nameserver_2 || 'julissa.ns.cloudflare.com'}

Steps to complete:
1. Log in to our domain provider (GoDaddy, Crazy Domains, Free Parking, etc.)
2. Navigate to the DNS Management or Name Servers section
3. Delete all existing nameservers
4. Add the two Cloudflare nameservers listed above
5. Save the changes

This change will allow Pump'd to manage our DNS settings through Cloudflare, which will enable our custom domain ordering system.

If you need any clarification or run into issues, please let me know.

Thank you!

Best regards,
${onboardingData?.contact_person || '[Your Name]'}`}
                              />
                            </div>
                          )}
                        </div>

                        {/* Step 3 */}
                        <div className="space-y-3">
                          <div className="p-3 space-y-1">
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-8 h-8 bg-brand-blue text-white text-sm rounded-full flex items-center justify-center font-semibold">
                                3
                              </div>
                              <span className="text-sm font-medium">Access Your Domain Nameserver Settings</span>
                            </div>
                            <div className="ml-12">
                              <p className="text-sm">
                                Log in to your domain provider (e.g., GoDaddy, Free Parking, Crazy Domains) and access the Nameserver management section
                              </p>
                            </div>
                          </div>
                          <div className="ml-11">
                            <div className="bg-gray-50 p-3 rounded border flex items-start gap-2">
                              <Settings className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-muted-foreground">
                                Look for "DNS Management", "DNS Records", or "Name Servers" in your domain control panel
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Step 4 */}
                        <div className="space-y-3">
                          <div className="p-3 space-y-1">
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 w-8 h-8 bg-brand-blue text-white text-sm rounded-full flex items-center justify-center font-semibold">
                                4
                              </div>
                              <span className="text-sm font-medium">Modify your Nameservers</span>
                            </div>
                            <div className="ml-12">
                              <p className="text-sm">
                                Delete your existing nameservers and replace them with these to allow us to manage your domain with Cloudflare
                              </p>
                            </div>
                          </div>
                          <div className="ml-11 space-y-3">
                            <CopyableNameserver 
                              nameserver={sectionData?.nameserver_1 || 'zod.ns.cloudflare.com'} 
                              label="Nameserver 1"
                            />
                            <CopyableNameserver 
                              nameserver={sectionData?.nameserver_2 || 'julissa.ns.cloudflare.com'} 
                              label="Nameserver 2"
                            />
                          </div>
                        </div>

                        {/* Helpful Links */}
                        <div className="bg-yellow-50 p-3 rounded border">
                          <h5 className="font-medium text-yellow-900 mb-2">Helpful Links</h5>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              <ExternalLink className="h-3 w-3 text-yellow-600" />
                              <a href="https://www.godaddy.com/help/change-nameservers-for-my-domains-664" target="_blank" rel="noopener noreferrer" className="text-yellow-700 hover:text-yellow-800 underline">
                                GoDaddy: Change nameservers guide
                              </a>
                            </div>
                            <div className="flex items-center gap-2">
                              <ExternalLink className="h-3 w-3 text-yellow-600" />
                              <a href="https://www.crazydomains.co.nz/help/article/how-to-add-delete-change-your-name-servers" target="_blank" rel="noopener noreferrer" className="text-yellow-700 hover:text-yellow-800 underline">
                                Crazy Domains: Nameserver changes
                              </a>
                            </div>
                            <div className="flex items-center gap-2">
                              <ExternalLink className="h-3 w-3 text-yellow-600" />
                              <a href="https://www.freeparking.co.nz/help/article/how-do-i-change-my-domain-name-servers" target="_blank" rel="noopener noreferrer" className="text-yellow-700 hover:text-yellow-800 underline">
                                Free Parking: How to change nameservers
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* What happens next for Nameserver Access */}
                      <div className="space-y-3 pb-4">
                        <h4 className="font-medium">What happens next:</h4>
                        <div className="space-y-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>Our team will configure all DNS settings through Cloudflare</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>We'll test and verify the domain is working correctly</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>You'll receive confirmation when the setup is complete</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>Your ordering page will become accessible from the subdomain you specified in step 1</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center cursor-pointer hover:bg-green-600 transition-colors" onClick={() => { onNavigateToSection?.(OnboardingSection.WEBSITE); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                              <ChevronRight className="h-4 w-4 stroke-white" />
                            </div>
                            <div className="flex-1">
                              <span>Proceed to website integration to let customers access this page from your website and start receiving orders! <a href="#" onClick={(e) => { e.preventDefault(); onNavigateToSection?.(OnboardingSection.WEBSITE); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-primary hover:underline">Go to Website Integration</a></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </TabsContent>
            </Tabs>

            {/* Action buttons */}
            <div className="flex items-center justify-between pt-6 border-t">
              <div className="flex items-center gap-2">
                {hasUnsavedChanges && (
                  <div className="text-sm text-amber-600 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    You have unsaved changes
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || isSaving}
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save Progress'}
                </Button>

                <Button
                  onClick={handleMarkComplete}
                  disabled={!canMarkComplete() || isMarkingComplete || hasUnsavedChanges}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  {isMarkingComplete ? 'Submitting...' : 'Mark as Complete'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DomainSetup;